/**
 * Utilitários de formatação para o e-commerce AesirShop
 * Em estrita conformidade com a Regra 8 (Sistema Multi-Moeda Dinâmico)
 */

const CURRENCY_LOCALES: Record<string, string> = {
  BRL: 'pt-BR',
  USD: 'en-US',
  EUR: 'pt-PT',
};

/**
 * Formata um valor numérico na moeda especificada pelas configurações da loja.
 * Nunca chumba moeda estática.
 */
export function formatCurrency(
  value: number | string | null | undefined,
  currencyCode: string = 'BRL'
): string {
  const numericValue = typeof value === 'number' ? value : parseFloat(String(value || '0'));
  const safeValue = isNaN(numericValue) ? 0 : numericValue;
  const code = (currencyCode || 'BRL').toUpperCase().trim();
  const locale = CURRENCY_LOCALES[code] || 'pt-BR';

  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: code,
    }).format(safeValue);
  } catch (error) {
    // Fallback seguro caso o código da moeda seja inválido para o runtime
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(safeValue);
  }
}

/**
 * Formata data e hora no padrão local
 */
export function formatDate(dateString: string | Date | null | undefined): string {
  if (!dateString) return '';
  try {
    const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
    return new Intl.DateTimeFormat('pt-BR', {
      dateStyle: 'short',
      timeStyle: 'short',
    }).format(date);
  } catch {
    return String(dateString);
  }
}

export interface StoreOpenStatus {
  isOpen: boolean;
  label: string;
}

/**
 * Avalia em tempo real se a loja física/virtual está aberta com base na Aba 10 do ERP
 */
export function getStoreOpenStatus(horarioFunc: any): StoreOpenStatus | null {
  if (!horarioFunc || !Array.isArray(horarioFunc.dias) || horarioFunc.dias.length < 7) {
    return null;
  }
  const now = new Date();
  const jsDay = now.getDay();
  // 0 = Segunda-feira, ..., 6 = Domingo
  const dayIndex = jsDay === 0 ? 6 : jsDay - 1;
  const daySchedule = horarioFunc.dias[dayIndex];

  if (!daySchedule || !daySchedule.status) {
    return { isOpen: false, label: 'Fechado hoje' };
  }

  const currentMinutes = now.getHours() * 60 + now.getMinutes();

  const toMinutes = (timeStr?: string) => {
    if (!timeStr) return null;
    const parts = timeStr.split(':').map(Number);
    if (parts.length !== 2 || isNaN(parts[0]) || isNaN(parts[1])) return null;
    return parts[0] * 60 + parts[1];
  };

  const openMin = toMinutes(daySchedule.abre);
  const closeMin = toMinutes(daySchedule.fecha);
  const lunchStart = toMinutes(daySchedule.almoco_inicio);
  const lunchEnd = toMinutes(daySchedule.almoco_fim);

  if (openMin === null || closeMin === null) {
    return null;
  }

  if (lunchStart !== null && lunchEnd !== null && currentMinutes >= lunchStart && currentMinutes < lunchEnd) {
    return { isOpen: false, label: `Almoço (volta ${daySchedule.almoco_fim || 'em breve'})` };
  }

  if (currentMinutes >= openMin && currentMinutes < closeMin) {
    return { isOpen: true, label: `Aberto agora (fecha às ${daySchedule.fecha})` };
  }

  if (currentMinutes < openMin) {
    return { isOpen: false, label: `Fechado (abre às ${daySchedule.abre})` };
  }

  return { isOpen: false, label: 'Fechado no momento' };
}
